import { BaseWebComponentElement } from '../../../framework/BaseWebComponentElement';
import { LicenseSubFormComponent } from '../../types/LicenseSubFormComponent';
import { LicensePrePaidSubFormController } from '../controller/LicensePrePaidSubFormController';
import { CompanyType } from '../../../company/types/company-type';
import { Observable } from 'rxjs';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { LicenseMainFormViewData } from '../../licenseMainForm/types/LicenseMainFormViewData';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { LicensePrePaidFormViewData } from '../types/LicensePrePaidFormViewData';
import { AddProductViewData } from '../../../company/common/addProductMenu/types/types';
import { CompanyData } from '../../../company/types/company-data';
import _ from 'lodash';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';

export class LicensePrePaidSubFormWebComponent extends BaseWebComponentElement implements LicenseSubFormComponent {
  public controller!: LicensePrePaidSubFormController;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: LicensePrePaidSubFormController,
    });
  }

  public get valid$(): Observable<boolean> {
    return this.controller.view.formGroup.valid$;
  }

  public get value(): FormGroupValue {
    const value = this.controller.view.formGroup.value;

    // adapt to format required by CompanyData
    _.set(value, ['specifics', 'licenses'], [_.get(value, ['specifics', 'license'])]);
    _.unset(value, ['specifics', 'license']);

    return value;
  }

  public getLicenseKeyFieldValue(): string {
    return this.controller.view.dom.$licensePrePaidFormField.value;
  }

  /**
   * Returns true, only if the "Check Validity" button has been clicked and the
   * license is valid
   */
  public getLicenseKeyValidity(): boolean {
    return this.controller.view.dom.$licensePrePaidFormField.Input.showValidIcon;
  }

  public setDisabledForLicenseKeyField(disabled: boolean) {
    if (this.controller) {
      this.controller.view.dom.$licensePrePaidFormField.disabled = disabled;
    }
  }

  public setVisibilityForInfoMessageForDisabledLicenseKeyField(
    canManageNetworks: boolean,
    canBeManagedByAbove: boolean,
    context: CompanyActionContext
  ) {
    const infoMessage = this.controller.view.dom.$infoMessageForDisabledLicenseKeyField;

    switch (context) {
      case CompanyActionContext.Create:
        infoMessage.textContent = this.dependencies.translateFn(
          'You can add license keys only to companies that manage their endpoint security. You can enable this option from the Company details screen.'
        );
        break;
      case CompanyActionContext.MyCompany:
        infoMessage.textContent = canBeManagedByAbove
          ? this.dependencies.translateFn(
              'You can add license keys only if you manage your endpoint security. Your partner can enable this option from the Company details screen.'
            )
          : this.dependencies.translateFn(
              'You can add license keys only if you manage your endpoint security. Enable partner assistance from the My company page so that they can enable this option for you. '
            );
        break;
      default:
        infoMessage.textContent = canBeManagedByAbove
          ? this.dependencies.translateFn(
              'You can add license keys only to companies that manage their endpoint security. You can enable this option from the Company details screen.'
            )
          : this.dependencies.translateFn(
              'You can add license keys only to companies that manage their endpoint security. To enable this option for them, ask the company to enable partner assistance from the My company page.'
            );
        break;
    }

    toggleVisibility(infoMessage, !canManageNetworks);
  }

  public resetLicensePrePaidForm() {
    this.controller.view.resetLicensePrePaidForm();
  }

  public setCompanyType(companyType: CompanyType) {
    this.controller.setCompanyType(companyType);
  }

  public resetValidators() {
    this.controller.view.resetLicenseKeyValidators();
  }

  public setViewData(viewData: LicenseMainFormViewData | AddProductViewData) {
    this.controller.view.setViewData(LicensePrePaidFormViewData.getLicensePrePaidFormViewData(viewData));
  }

  public setCompanyId(companyId: string): void {
    this.controller.view.setCompanyId(companyId);
  }

  public setCompanyData(company: CompanyData): void {}

  public setCanBeManagedByAbove(canBeManagedByAbove: boolean) {
    if (!this.started) {
      return;
    }

    this.controller.view.setCanBeManagedByAbove(canBeManagedByAbove);
    if (!canBeManagedByAbove) {
      // reset the form if manage by above was disabled
      this.resetLicensePrePaidForm();
    }
  }

  public updateViewData(viewData: LicenseMainFormViewData | AddProductViewData) {
    this.controller.view.updateViewData(LicensePrePaidFormViewData.getLicensePrePaidFormViewData(viewData));
  }

  public removeRequiredOnLicenseKeyField() {
    this.controller.view.dom.$licensePrePaidFormField.removeAttribute('required');
  }

  public isLicenseKeyFieldEmpty(): boolean {
    return this.controller.view.dom.$licensePrePaidFormField.value.length === 0;
  }

  set hasStackedFormFields(value: boolean) {
    if (value) {
      this.setAttribute('hasStackedFormFields', '');
    } else {
      this.removeAttribute('hasStackedFormFields');
    }
  }

  get hasStackedFormFields(): boolean {
    return this.hasAttribute('hasStackedFormFields');
  }

  get hasIncompatibilityError() {
    return this.controller.hasCompatibilityIssues;
  }

  get hasIssues() {
    return this.controller.hasIssues;
  }
}
